'use client';

/**
 * ProtocolGraveCard Component
 *
 * Displays a dead/abandoned protocol as a tombstone card
 * in the graveyard view with spooky theming.
 */
import { c as _c } from "react/compiler-runtime";
import { motion } from 'motion/react';
import Image from 'next/image';
import Link from 'next/link';
import { Card, CardHeader, CardBody, CardFooter, Button, Chip, Tooltip } from '@heroui/react';
import { Skull, AlertTriangle, Calendar, Layers, Search, ExternalLink } from 'lucide-react';
import type { HallOfShameProtocol } from '@/lib/masterchef-cache/types';
interface ProtocolGraveCardProps {
  protocol: HallOfShameProtocol;
  userHasFunds: boolean;
  onCheckFunds: () => void;
  /** Index for staggered animation timing */
  index?: number;
}
const statusConfig = {
  deprecated: {
    label: 'Deprecated',
    color: 'default' as const,
    icon: Calendar,
    description: 'No longer maintained'
  },
  abandoned: {
    label: 'Abandoned',
    color: 'warning' as const,
    icon: AlertTriangle,
    description: 'Team disappeared'
  },
  rugged: {
    label: 'Rugged',
    color: 'danger' as const,
    icon: Skull,
    description: 'Exit scam confirmed'
  }
};

// "Rise from the Grave" animation variants
const graveRiseVariants = {
  hidden: {
    opacity: 0,
    y: 80,
    rotateX: -15,
    scale: 0.9
  },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    rotateX: 0,
    scale: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 100,
      damping: 15,
      delay: index * 0.1 // Staggered entrance
    }
  }),
  hover: {
    y: -8,
    scale: 1.02,
    rotateZ: 1,
    // Slight tilt on hover
    transition: {
      type: 'spring' as const,
      stiffness: 400,
      damping: 25
    }
  }
};
export default function ProtocolGraveCard(t0) {
  const $ = _c(68);
  const {
    protocol,
    userHasFunds,
    onCheckFunds,
    index: t1
  } = t0;
  const index = t1 === undefined ? 0 : t1;
  const status = statusConfig[protocol.protocol.status as keyof typeof statusConfig] || statusConfig.abandoned;
  const StatusIcon = status.icon;
  const totalContracts = protocol.contracts.length;
  const totalActivePools = protocol.totalNonEmptyPools;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      perspective: 1000
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = `
          relative overflow-hidden content-center align-middle 
          bg-gradient-to-b from-content1 to-content2 rounded-t-full rounded-b-xl
          border-2 inset-shadow-xl/50 max-w-[380px] ${userHasFunds ? "border-danger" : "border-divider"}
          ${userHasFunds ? "ring-2 ring-danger/50 ring-offset-2 ring-offset-background" : ""}
        `;
  let t4;
  if ($[1] !== userHasFunds) {
    t4 = userHasFunds && <div className="absolute top-2 right-2 z-20"><motion.div animate={{
        scale: [1, 1.1, 1]
      }} transition={{
        duration: 1.5,
        repeat: Infinity
      }}><Chip size="sm" color="danger" variant="solid" startContent={<AlertTriangle className="w-3 h-3" />}>Funds Detected!</Chip></motion.div></div>;
    $[1] = userHasFunds;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== protocol.protocol.logoUrl || $[4] !== protocol.protocol.name) {
    t5 = protocol.protocol.logoUrl ? <Image src={protocol.protocol.logoUrl} alt={protocol.protocol.name} fill={true} className="object-cover grayscale opacity-70" /> : <Skull className="w-8 h-8 text-foreground-400" />;
    $[3] = protocol.protocol.logoUrl;
    $[4] = protocol.protocol.name;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50 flex items-end justify-center pb-1"><span className="text-[8px] font-bold text-white/70">R.I.P</span></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t5) {
    t7 = <div className="relative w-14 h-14 rounded-xl bg-content2 flex items-center justify-center overflow-hidden border border-divider">{t5}{t6}</div>;
    $[7] = t5;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== protocol.protocol.name) {
    t8 = <h3 className="text-lg font-bold text-foreground-700">{protocol.protocol.name}</h3>;
    $[9] = protocol.protocol.name;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== StatusIcon) {
    t9 = <StatusIcon className="w-3 h-3" />;
    $[11] = StatusIcon;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== status.color || $[14] !== status.label || $[15] !== t9) {
    t10 = <Chip size="sm" color={status.color} variant="flat" startContent={t9}>{status.label}</Chip>;
    $[13] = status.color;
    $[14] = status.label;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== status.description || $[18] !== t10) {
    t11 = <div className="flex items-center gap-2 mt-1"><Tooltip content={status.description}>{t10}</Tooltip></div>;
    $[17] = status.description;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t8) {
    t12 = <div className="flex flex-col flex-1">{t8}{t11}</div>;
    $[20] = t11;
    $[21] = t8;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t7) {
    t13 = <CardHeader className="flex flex-col content-center align-middle justify-center gap-3 pt-8 pb-[25px] m-auto w-full">{t7}{t12}</CardHeader>;
    $[23] = t12;
    $[24] = t7;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== protocol.protocol.statusReason) {
    t14 = protocol.protocol.statusReason && <p className="text-sm text-foreground-500 italic mb-6">“{protocol.protocol.statusReason}”</p>;
    $[26] = protocol.protocol.statusReason;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Layers className="w-4 h-4" />;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const t16 = totalContracts !== 1 ? "s" : "";
  let t17;
  if ($[29] !== t16 || $[30] !== totalContracts) {
    t17 = <Tooltip content="Number of MasterChef contracts"><div className="flex items-center gap-1.5 text-sm text-foreground-500">{t15}<span>{totalContracts} Contract{t16}</span></div></Tooltip>;
    $[29] = t16;
    $[30] = totalContracts;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span className="w-2 h-2 rounded-full bg-warning" />;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const t19 = totalActivePools !== 1 ? "s" : "";
  let t20;
  if ($[33] !== t19 || $[34] !== totalActivePools) {
    t20 = <Tooltip content="Pools that still have liquidity"><div className="flex items-center gap-1.5 text-sm text-foreground-500">{t18}<span>{totalActivePools} Active Pool{t19}</span></div></Tooltip>;
    $[33] = t19;
    $[34] = totalActivePools;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t17 || $[37] !== t20) {
    t21 = <div className="flex flex-wrap gap-3">{t17}{t20}</div>;
    $[36] = t17;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== protocol.contracts || $[40] !== totalContracts) {
    t22 = totalContracts > 0 && <div className="mt-3 p-2 bg-content2 rounded-lg"><div className="text-xs text-foreground-400 mb-1">Contracts:</div>{protocol.contracts.slice(0, 2).map(_temp)}{totalContracts > 2 && <div className="text-xs text-foreground-400 mt-1">+{totalContracts - 2} more...</div>}</div>;
    $[39] = protocol.contracts;
    $[40] = totalContracts;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t14 || $[43] !== t21 || $[44] !== t22) {
    t23 = <CardBody className="pt-2 pb-3">{t14}{t21}{t22}</CardBody>;
    $[42] = t14;
    $[43] = t21;
    $[44] = t22;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  const t24 = userHasFunds ? "danger" : "warning";
  const t25 = userHasFunds ? "solid" : "flat";
  let t26;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <Search className="w-4 h-4" />;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  const t27 = userHasFunds ? "Rescue My Funds!" : "Check for Stuck Funds";
  let t28;
  if ($[47] !== onCheckFunds || $[48] !== t24 || $[49] !== t25 || $[50] !== t27) {
    t28 = <Button fullWidth={true} color={t24} variant={t25} className="font-semibold" onPress={onCheckFunds} startContent={t26}>{t27}</Button>;
    $[47] = onCheckFunds;
    $[48] = t24;
    $[49] = t25;
    $[50] = t27;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  const t29 = `/rescue/${protocol.protocol.slug}`;
  let t30;
  if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <ExternalLink className="w-3 h-3" />;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== t29) {
    t31 = <Button as={Link} href={t29} fullWidth={true} variant="light" className="font-medium text-foreground-500" endContent={t30}>More Info</Button>;
    $[53] = t29;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] !== t28 || $[56] !== t31) {
    t32 = <CardFooter className="pt-0 flex-col gap-2">{t28}{t31}</CardFooter>;
    $[55] = t28;
    $[56] = t31;
    $[57] = t32;
  } else {
    t32 = $[57];
  }
  let t33;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-t from-green-900/30 to-transparent" />;
    $[58] = t33;
  } else {
    t33 = $[58];
  }
  let t34;
  if ($[59] !== t13 || $[60] !== t23 || $[61] !== t3 || $[62] !== t32 || $[63] !== t4) {
    t34 = <Card className={t3}>{t4}{t13}{t23}{t32}{t33}</Card>;
    $[59] = t13;
    $[60] = t23;
    $[61] = t3;
    $[62] = t32;
    $[63] = t4;
    $[64] = t34;
  } else {
    t34 = $[64];
  }
  let t35;
  if ($[65] !== index || $[66] !== t34) {
    t35 = <motion.div custom={index} variants={graveRiseVariants} initial="hidden" animate="visible" whileHover="hover" style={t2}>{t34}</motion.div>;
    $[65] = index;
    $[66] = t34;
    $[67] = t35;
  } else {
    t35 = $[67];
  }
  return t35;
}
function _temp(contractData) {
  return <div key={contractData.contract.contractAddress} className="text-xs text-foreground-500 truncate">{contractData.contract.label}:{" "}<span className="font-mono text-foreground-400">{contractData.contract.contractAddress.slice(0, 10)}...</span></div>;
}